import { useState, useRef, useEffect, useCallback } from 'react';
import { GoogleGenAI, LiveServerMessage, Modality, FunctionDeclaration, Type } from '@google/genai';
import { BotConfig } from '../types';
import { createPcmBlob, decodeAudioData, base64ToUint8Array } from '../utils/audioUtils';

interface UseLiveApiProps {
  config: BotConfig;
  isOpen: boolean; // Only connect when the widget is open
  apiKey?: string;
}

const WEBHOOK_TOOL_NAME = 'consult_webhook';

const webhookToolDeclaration: FunctionDeclaration = {
  name: WEBHOOK_TOOL_NAME,
  description: 'Consult an external system for information or actions not in your knowledge base. Use this whenever the user asks for real-time data or specific external information.',
  parameters: {
    type: Type.OBJECT,
    properties: {
      query: {
        type: Type.STRING,
        description: 'The query or keyword to send to the external webhook.',
      },
    },
    required: ['query'],
  },
};

export const useLiveApi = ({ config, isOpen, apiKey }: UseLiveApiProps) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false); // User is speaking
  const [isBotSpeaking, setIsBotSpeaking] = useState(false); // Bot is speaking
  const [volume, setVolume] = useState(0);
  const [transcripts, setTranscripts] = useState<{ role: 'user' | 'model'; text: string }[]>([]);
  const [error, setError] = useState<string | null>(null);

  const sessionPromiseRef = useRef<Promise<any> | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const inputContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const audioSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
  const nextStartTimeRef = useRef<number>(0);
  const lastVolumeUpdateRef = useRef<number>(0);
  
  // Temporary buffers for transcription
  const currentInputTransRef = useRef('');
  const currentOutputTransRef = useRef('');

  const cleanupAudio = useCallback(() => {
    // Stop all playing sources
    audioSourcesRef.current.forEach((source) => {
      try {
        source.stop();
      } catch (e) {
        // ignore
      }
    });
    audioSourcesRef.current.clear();

    // Close input stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    // Disconnect processor
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    
    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }

    // Close contexts
    if (inputContextRef.current) {
      inputContextRef.current.close();
      inputContextRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    sessionPromiseRef.current = null;
    setIsConnected(false);
    setIsSpeaking(false);
    setIsBotSpeaking(false);
    setVolume(0);
  }, []);

  const connect = useCallback(async () => {
    const key = apiKey || process.env.API_KEY;
    if (!key) {
      const msg = "API Key missing. Check Vercel Env Vars.";
      console.error(msg);
      setError(msg);
      return;
    }

    try {
      cleanupAudio();
      setError(null);

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("Audio recording is not supported in this browser.");
      }

      // Request Mic Access first
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;
        
        // Log devices for debugging
        const devices = await navigator.mediaDevices.enumerateDevices();
        console.log("Available devices:", devices);
      } catch (e: any) {
        if (e.name === 'NotFoundError' || e.message === 'Requested device not found') {
            throw new Error("Microphone not found. Please connect a microphone.");
        }
        if (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError') {
            throw new Error("Microphone permission denied. Please allow access.");
        }
        throw e;
      }

      // Initialize Audio Contexts after stream is acquired
      // Input: 16kHz for Gemini
      const inputCtx = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 16000 });
      // Output: 24kHz for Gemini
      const outputCtx = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
      
      // Resume contexts immediately to avoid autoplay blocks
      await inputCtx.resume();
      await outputCtx.resume();

      inputContextRef.current = inputCtx;
      audioContextRef.current = outputCtx;
      nextStartTimeRef.current = outputCtx.currentTime;

      const ai = new GoogleGenAI({ apiKey: key });
      
      // For Live API, Search Grounding might not be fully supported in the same way as Text Chat.
      // So we prioritize Webhook if it exists, but we DO pass the Sheets URL in the instructions for context.
      const tools = config.webhookUrl ? [{ functionDeclarations: [webhookToolDeclaration] }] : [];
      
      let modifiedInstruction = config.systemInstruction;
      if (config.googleSheets && config.googleSheets.length > 0) {
          modifiedInstruction += `\n\n[REFERENCE MATERIALS]: You have access to the following Google Sheets for reference:\n${config.googleSheets.map(url => `- ${url}`).join('\n')}\nNote: I cannot browse them in real-time in this voice mode, but I know of their existence.`;
      }

      const sessionPromise = ai.live.connect({
        model: 'gemini-2.5-flash-native-audio-preview-09-2025',
        config: {
          responseModalities: [Modality.AUDIO],
          speechConfig: {
            voiceConfig: { prebuiltVoiceConfig: { voiceName: config.voice } },
          },
          systemInstruction: modifiedInstruction,
          inputAudioTranscription: {},
          outputAudioTranscription: {},
          tools: tools,
        },
        callbacks: {
          onopen: () => {
            setIsConnected(true);
            
            // Setup Audio Pipeline
            const source = inputCtx.createMediaStreamSource(stream);
            sourceRef.current = source;
            
            // OPTIMIZATION: Reduced buffer size to 512
            // 512 samples @ 16kHz = 32ms latency
            const processor = inputCtx.createScriptProcessor(512, 1, 1);
            processorRef.current = processor;

            processor.onaudioprocess = (e) => {
              const inputData = e.inputBuffer.getChannelData(0);
              
              // Throttle volume updates
              const now = Date.now();
              if (now - lastVolumeUpdateRef.current > 100) {
                let sum = 0;
                for(let i = 0; i < inputData.length; i += 4) sum += inputData[i] * inputData[i];
                const rms = Math.sqrt(sum / (inputData.length / 4));
                setVolume(Math.min(rms * 5, 1)); 
                setIsSpeaking(rms > 0.01);
                lastVolumeUpdateRef.current = now;
              }

              const pcmBlob = createPcmBlob(inputData);
              sessionPromise.then((session: any) => {
                session.sendRealtimeInput({ media: pcmBlob });
              });
            };

            source.connect(processor);
            processor.connect(inputCtx.destination);
          },
          onmessage: async (message: LiveServerMessage) => {
            // Handle Tool Calls (Webhook)
            if (message.toolCall) {
              for (const fc of message.toolCall.functionCalls) {
                if (fc.name === WEBHOOK_TOOL_NAME && config.webhookUrl) {
                   try {
                     const response = await fetch(config.webhookUrl, {
                       method: 'POST',
                       headers: { 'Content-Type': 'application/json' },
                       body: JSON.stringify({ query: (fc.args as any).query })
                     });
                     const data = await response.json();
                     const result = JSON.stringify(data);
                     
                     sessionPromise.then((session: any) => {
                       session.sendToolResponse({
                         functionResponses: [{
                           id: fc.id,
                           name: fc.name,
                           response: { result: result }
                         }]
                       });
                     });
                   } catch (e: any) {
                     console.error("Webhook error", e);
                     sessionPromise.then((session: any) => {
                        session.sendToolResponse({
                          functionResponses: [{
                            id: fc.id,
                            name: fc.name,
                            response: { result: `Error calling webhook: ${e.message}` }
                          }]
                        });
                      });
                   }
                }
              }
            }

            // Handle Audio Output
            const base64Audio = message.serverContent?.modelTurn?.parts?.[0]?.inlineData?.data;
            if (base64Audio && audioContextRef.current) {
              const ctx = audioContextRef.current;
              
              if (nextStartTimeRef.current < ctx.currentTime) {
                nextStartTimeRef.current = ctx.currentTime + 0.01;
              }
              
              const audioBuffer = await decodeAudioData(
                base64ToUint8Array(base64Audio),
                ctx,
                24000,
                1
              );
              
              const source = ctx.createBufferSource();
              source.buffer = audioBuffer;
              source.connect(ctx.destination);
              
              source.onended = () => {
                audioSourcesRef.current.delete(source);
                if (audioSourcesRef.current.size === 0) {
                   setIsBotSpeaking(false);
                }
              };
              
              source.start(nextStartTimeRef.current);
              nextStartTimeRef.current += audioBuffer.duration;
              audioSourcesRef.current.add(source);
              setIsBotSpeaking(true);
            }

            // Handle Transcriptions
            if (message.serverContent?.outputTranscription) {
               currentOutputTransRef.current += message.serverContent.outputTranscription.text;
            }
            if (message.serverContent?.inputTranscription) {
               currentInputTransRef.current += message.serverContent.inputTranscription.text;
            }

            if (message.serverContent?.turnComplete) {
               if (currentInputTransRef.current) {
                 setTranscripts(prev => [...prev, { role: 'user', text: currentInputTransRef.current }]);
                 currentInputTransRef.current = '';
               }
               if (currentOutputTransRef.current) {
                 setTranscripts(prev => [...prev, { role: 'model', text: currentOutputTransRef.current }]);
                 currentOutputTransRef.current = '';
               }
            }

            // Handle Interruption
            if (message.serverContent?.interrupted) {
              audioSourcesRef.current.forEach(src => src.stop());
              audioSourcesRef.current.clear();
              nextStartTimeRef.current = 0;
              setIsBotSpeaking(false);
              currentOutputTransRef.current = ''; 
            }
          },
          onclose: () => {
            setIsConnected(false);
          },
          onerror: (err) => {
            console.error(err);
            setError("Connection lost. Please retry.");
            setIsConnected(false);
          }
        }
      });
      
      sessionPromiseRef.current = sessionPromise;

    } catch (err: any) {
      console.error(err);
      setError(err.message || "Failed to connect microphone");
    }
  }, [config, cleanupAudio, apiKey]);

  // Connect when isOpen becomes true, disconnect when false
  useEffect(() => {
    if (isOpen && !isConnected) {
      // Wait for gesture usually, managed by component
    } else if (!isOpen && isConnected) {
      cleanupAudio();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => cleanupAudio();
  }, [cleanupAudio]);

  return {
    isConnected,
    isSpeaking,
    isBotSpeaking,
    volume,
    transcripts,
    error,
    reconnect: connect
  };
};